import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, COMPANY, SERVICES } from "@/lib/site";

const empty = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service: "",
  message: "",
};

export default function ContactSection() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name.trim() || !form.email.trim() || !form.service || !form.message.trim()) {
      toast.error("Please fill all required fields.");
      return;
    }
    if (form.message.trim().length < 5) {
      toast.error("Message must be at least 5 characters.");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/inquiries", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        service: form.service,
        message: form.message.trim(),
      });
      toast.success("Inquiry received. Our team will be in touch shortly.");
      setForm(empty);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data?.detail) ? err.response.data.detail[0]?.msg : null) ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your entries and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-slate-50 py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="eyebrow mb-4">Contact</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 leading-tight">
              Talk to a pharma services specialist.
            </h2>
            <p className="mt-6 text-slate-600 text-lg leading-relaxed">
              Share your project brief — sourcing need, audit scope, compliance concern, characterization requirement
              or stability program — and we&rsquo;ll respond within one business day.
            </p>

            <div className="mt-8 p-5 border border-slate-200 bg-white">
              <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium">Point of contact</div>
              <div className="mt-2 font-display text-lg font-semibold text-slate-900" data-testid="contact-director-name">
                {COMPANY.director}
              </div>
              <div className="text-sm text-slate-600">{COMPANY.directorTitle}, {COMPANY.legalName}</div>
            </div>

            <div className="mt-8 space-y-6">
              <ContactRow icon={MapPin} label="Head office" value={COMPANY.address} />
              <ContactRow
                icon={Phone}
                label="Mobile"
                value={COMPANY.phone}
                href={`tel:${COMPANY.phone.replace(/\s|\(|\)|-/g, "")}`}
              />
              <ContactRow
                icon={WhatsappIcon}
                label="WhatsApp"
                value={COMPANY.whatsapp}
                href={`https://wa.me/${COMPANY.whatsapp.replace(/\s|\+|\(|\)|-/g, "")}`}
                iconBrand
              />
              <ContactRow icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} />
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            onSubmit={onSubmit}
            noValidate
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7 bg-white border border-slate-200 p-8 lg:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full name" required>
                <Input
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={setField("name")}
                  placeholder="Jane Doe"
                  autoComplete="name"
                  required
                  className="rounded-none border-slate-300 focus-visible:ring-teal-500 focus-visible:ring-2 h-11"
                />
              </Field>
              <Field label="Work email" required>
                <Input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={setField("email")}
                  placeholder="jane@company.com"
                  autoComplete="email"
                  required
                  className="rounded-none border-slate-300 focus-visible:ring-teal-500 focus-visible:ring-2 h-11"
                />
              </Field>
              <Field label="Phone">
                <Input
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={setField("phone")}
                  placeholder="+1 555 0100"
                  autoComplete="tel"
                  className="rounded-none border-slate-300 focus-visible:ring-teal-500 focus-visible:ring-2 h-11"
                />
              </Field>
              <Field label="Company">
                <Input
                  data-testid="contact-input-company"
                  value={form.company}
                  onChange={setField("company")}
                  placeholder="Acme Pharmaceuticals"
                  autoComplete="organization"
                  className="rounded-none border-slate-300 focus-visible:ring-teal-500 focus-visible:ring-2 h-11"
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Service of interest" required>
                  <Select value={form.service} onValueChange={setField("service")}>
                    <SelectTrigger
                      data-testid="contact-select-service"
                      className="rounded-none border-slate-300 focus:ring-teal-500 focus:ring-2 h-11"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem
                          key={s.id}
                          value={s.title}
                          data-testid={`contact-service-option-${s.id}`}
                        >
                          {s.title}
                        </SelectItem>
                      ))}
                      <SelectItem value="Other / General" data-testid="contact-service-option-other">
                        Other / General inquiry
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Message" required>
                  <Textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={setField("message")}
                    rows={5}
                    placeholder="Tell us about your project, timelines and any regulatory context..."
                    required
                    className="rounded-none border-slate-300 focus-visible:ring-teal-500 focus-visible:ring-2 resize-none"
                  />
                </Field>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-slate-500">
                By submitting, you agree to be contacted about your inquiry. We respect confidentiality.
              </p>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={submitting}
                className="inline-flex items-center justify-center bg-teal-700 hover:bg-teal-800 text-white px-8 py-3.5 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" strokeWidth={2} />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Inquiry
                    <Send className="ml-2 w-4 h-4" strokeWidth={2} />
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs uppercase tracking-[0.15em] font-medium text-slate-700">
        {label}
        {required && <span className="text-teal-700 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, iconBrand }) {
  const content = (
    <div className="flex items-start gap-4">
      <span
        className={`inline-flex items-center justify-center w-10 h-10 border ${
          iconBrand ? "bg-[#25D366] border-[#25D366] text-white" : "bg-white border-slate-200 text-teal-700"
        }`}
      >
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </span>
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium">{label}</div>
        <div className="mt-1 text-slate-800 font-medium">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target={iconBrand ? "_blank" : undefined} rel={iconBrand ? "noreferrer" : undefined} className="block hover:opacity-80 transition-opacity">
      {content}
    </a>
  ) : (
    content
  );
}

// Official-style WhatsApp glyph as inline SVG so it matches lucide sizing / stroke API.
function WhatsappIcon({ className = "", ...props }) {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      fill="currentColor"
      aria-hidden="true"
      {...props}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.198.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.075-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
      <path d="M20.52 3.449C18.24 1.245 15.24 0 12.05 0 5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.52 3.449zM12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.981.998-3.648-.235-.374a9.86 9.86 0 0 1-1.511-5.26c.002-5.45 4.437-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.884 9.885z"/>
    </svg>
  );
}
